import React, { useEffect, useState, useCallback } from "react";
import { Wand2, Coins, Sparkles, Send, Loader2, Gift, X } from "lucide-react";
import { sr } from "../../lib/sr";

const fieldStyle = { width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, fontFamily: "inherit" };

export default function AiAdStudio() {
  const [status, setStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState(null);
  const [brief, setBrief] = useState("");
  const [draft, setDraft] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [history, setHistory] = useState([]);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [topUp, setTopUp] = useState(false);
  const [amount, setAmount] = useState(50);

  const loadStatus = useCallback(() => {
    sr.aiAdChannels().then((d) => { setChannels(d.channels || []); setStatus(d.status); if (!channel && d.channels?.length) setChannel(d.channels[0].key); }).catch(() => {});
  }, [channel]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Handle Stripe return (?credits_session=... / ?credits_canceled=1)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sid = p.get("credits_session");
    if (sid) {
      sr.creditsCheckoutStatus(sid).then((r) => {
        setMsg(r.payment_status === "paid" ? { ok: true, text: `Payment received — balance is now ${r.balance} credits.` } : { ok: false, text: "Payment is still processing…" });
        loadStatus();
      }).catch(() => {});
      window.history.replaceState({}, "", "/app/ai-ad");
    } else if (p.get("credits_canceled")) {
      setMsg({ ok: false, text: "Credit purchase canceled." });
      window.history.replaceState({}, "", "/app/ai-ad");
    }
  }, [loadStatus]);

  const fw = status?.free_window;
  const price = status?.ad_gen_price ?? 2;
  const genLabel = fw?.active ? "Free" : `${price} credits`;

  const handle402 = (e) => {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 402) { setTopUp(true); setMsg({ ok: false, text: d?.message || "Out of credits." }); return true; }
    setMsg({ ok: false, text: "Something went wrong. Please try again." });
    return false;
  };

  const generate = async () => {
    if (!channel) return;
    setBusy(true); setMsg(null);
    try {
      const r = await sr.aiAdGenerate(channel, brief);
      setDraft(r.draft); setDraftId(r.draft_id); setHistory([]); setStatus(r.status);
      if (r.charge?.charged) setMsg({ ok: true, text: `Generated — ${r.charge.price} credits used. Balance ${r.charge.balance}.` });
    } catch (e) { handle402(e); } finally { setBusy(false); }
  };

  const sendEdit = async () => {
    if (!instruction.trim() || !draftId) return;
    setBusy(true); setMsg(null);
    try {
      const r = await sr.aiAdEdit(draftId, instruction.trim());
      setDraft(r.draft); setHistory(r.history || []); setStatus(r.status); setInstruction("");
      if (r.charge?.charged) setMsg({ ok: true, text: `Updated — ${r.charge.price} credits used. Balance ${r.charge.balance}.` });
    } catch (e) { handle402(e); } finally { setBusy(false); }
  };

  const buyCredits = async () => {
    try { const r = await sr.creditsCheckout(Number(amount), window.location.origin); window.location.href = r.checkout_url; }
    catch { setMsg({ ok: false, text: "Could not start checkout." }); }
  };

  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updScript = (i, v) => setDraft((d) => ({ ...d, script: d.script.map((s, idx) => idx === i ? { ...s, text: v } : s) }));

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "4px 0 40px" }} data-testid="ai-ad-studio">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Wand2 size={22} color="var(--navy)" />
        <h1 style={{ fontSize: 24, margin: 0, color: "var(--navy)" }}>AI Ad Creation</h1>
      </div>
      <p className="sr-muted" style={{ fontSize: 14, marginTop: 4 }}>Generate channel-ready ad creative with AI, then chat to refine it.</p>

      {/* Credit / free-window strip */}
      <div className="sr-card" data-testid="ai-ad-credit-strip" style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {fw?.active ? (
          <span className="sr-badge sr-badge-green" data-testid="ai-ad-free-badge"><Gift size={13} /> Free · {fw.days_remaining} day{fw.days_remaining === 1 ? "" : "s"} left</span>
        ) : (
          <>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "var(--navy)" }} data-testid="ai-ad-balance"><Coins size={16} /> {status?.balance ?? 0} credits</span>
            <span className="sr-muted" style={{ fontSize: 13 }}>{price} credits per generation</span>
            <button className="sr-btn sr-btn-primary" data-testid="ai-ad-buy-credits" style={{ marginLeft: "auto" }} onClick={() => setTopUp(true)}>Buy credits</button>
          </>
        )}
        {fw?.active && <span className="sr-muted" style={{ fontSize: 13 }}>Your first 30 days of AI ad creation are on us.</span>}
      </div>

      {msg && (
        <div data-testid="ai-ad-msg" style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 13, background: msg.ok ? "#dcfce7" : "#fee2e2", color: msg.ok ? "#166534" : "#991b1b" }}>{msg.text}</div>
      )}

      {/* Channel + brief */}
      <div className="sr-card" style={{ marginTop: 14 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 8 }}>1 · Pick a channel</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="ai-ad-channels">
          {channels.map((c) => (
            <button key={c.key} data-testid={`ai-ad-channel-${c.key}`} onClick={() => setChannel(c.key)}
              className={`sr-badge ${channel === c.key ? "sr-badge-blue" : "sr-badge-gray"}`}
              style={{ cursor: "pointer", border: channel === c.key ? "1px solid var(--blue)" : "1px solid transparent", padding: "6px 12px" }}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="sr-eyebrow" style={{ margin: "14px 0 6px" }}>2 · Describe the ad</div>
        <textarea data-testid="ai-ad-brief" value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
          placeholder="What are you promoting? Offer, audience, key message…" style={fieldStyle} />
        <button className="sr-btn sr-btn-primary" data-testid="ai-ad-generate" onClick={generate} disabled={busy || !channel} style={{ marginTop: 12 }}>
          {busy ? <Loader2 size={15} className="sr-spin" /> : <Sparkles size={15} />} Generate · {genLabel}
        </button>
      </div>

      {/* Draft + chat-to-edit */}
      {draft && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="ai-ad-draft">
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Your ad draft · {draft.channel}</div>

          {Array.isArray(draft.headlines) && draft.headlines.length > 0 && draft.headlines.map((h, i) => (
            <input key={i} data-testid={`ai-ad-headline-${i}`} value={h} onChange={(e) => setDraft((d) => ({ ...d, headlines: d.headlines.map((x, idx) => idx === i ? e.target.value : x) }))} style={{ ...fieldStyle, marginBottom: 8 }} />
          ))}
          {draft.headline != null && (
            <input data-testid="ai-ad-headline" value={draft.headline} onChange={(e) => upd("headline", e.target.value)} style={{ ...fieldStyle, fontWeight: 700, marginBottom: 8 }} />
          )}
          {Array.isArray(draft.script) && draft.script.length > 0 && (
            <div data-testid="ai-ad-script" style={{ marginBottom: 8 }}>
              {draft.script.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
                  <span className="sr-badge sr-badge-gray" style={{ fontSize: 10, minWidth: 70, textAlign: "center" }}>{s.type}</span>
                  <textarea value={s.text} onChange={(e) => updScript(i, e.target.value)} rows={2} style={fieldStyle} />
                </div>
              ))}
            </div>
          )}
          {draft.body != null && (
            <textarea data-testid="ai-ad-body" value={draft.body} onChange={(e) => upd("body", e.target.value)} rows={3} style={{ ...fieldStyle, marginBottom: 8 }} />
          )}
          {draft.cta != null && (
            <input data-testid="ai-ad-cta" value={draft.cta} onChange={(e) => upd("cta", e.target.value)} style={{ ...fieldStyle, marginBottom: 8 }} placeholder="Call to action" />
          )}
          {draft.notes && <p className="sr-muted" style={{ fontSize: 12 }}>{draft.notes}</p>}

          {/* Chat-to-edit */}
          <div style={{ marginTop: 12, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
            <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Chat to edit · {genLabel} per change</div>
            {history.length > 0 && (
              <div data-testid="ai-ad-history" style={{ marginBottom: 8 }}>
                {history.map((h, i) => (
                  <div key={i} className="sr-muted" style={{ fontSize: 12, marginBottom: 2 }}>↳ {h.instruction}</div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="ai-ad-edit-input" value={instruction} onChange={(e) => setInstruction(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendEdit()}
                placeholder="e.g. Make it punchier and mention free shipping" style={fieldStyle} />
              <button className="sr-btn sr-btn-primary" data-testid="ai-ad-edit-send" onClick={sendEdit} disabled={busy || !instruction.trim()}>
                {busy ? <Loader2 size={15} className="sr-spin" /> : <Send size={15} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top-up modal */}
      {topUp && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={() => setTopUp(false)}>
          <div className="sr-card" data-testid="ai-ad-topup" style={{ width: 380, maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Coins size={18} color="var(--navy)" />
              <h3 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>Buy SmartReach Credits</h3>
              <button onClick={() => setTopUp(false)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>1 credit = $1. Each AI ad generation costs {price} credits.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              {[25, 50, 100, 250].map((a) => (
                <button key={a} data-testid={`ai-ad-pack-${a}`} onClick={() => setAmount(a)} className={`sr-badge ${Number(amount) === a ? "sr-badge-blue" : "sr-badge-gray"}`} style={{ cursor: "pointer", padding: "8px 10px" }}>${a}</button>
              ))}
            </div>
            <input data-testid="ai-ad-topup-amount" type="number" min={25} value={amount} onChange={(e) => setAmount(e.target.value)} style={{ ...fieldStyle, marginTop: 10 }} />
            <button className="sr-btn sr-btn-primary" data-testid="ai-ad-topup-checkout" onClick={buyCredits} style={{ marginTop: 12, width: "100%" }}>Pay ${amount} with card</button>
          </div>
        </div>
      )}
    </div>
  );
}
